// ═══ AUREUS SOCIAL PRO — Constantes flexi-jobs ═══
// Module 01 — Flexi-jobs (CP 302 Horeca + tous secteurs éligibles)
// Dernière mise à jour des constantes : 2026-03-01
// Sources officielles : ONSS, SPF Finances, Moniteur belge 30/12/2025,
// Prestataire B, Prestataire C 28/01/2026, Prestataire D 23/02/2026
// NOTE: pas de "use client" — lib isomorphique (server + client)
//
// ⚠️ DEUX SOURCES DE VÉRITÉ POUR LE MÊME CHIFFRE — corrigé le 03/09/2026.
// Ce fichier gardait sa PROPRE copie du flexi-salaire — valeurs historiques,
// périmées depuis le 01/09/2026 : 11,87 €/h et 21 €/h.
// `lois-belges.js` en gardait une autre, tenue à jour. Le 01/09/2026 le SPF a
// indexé de 2 % : `lois-belges.js` a suivi le lendemain, celui-ci non — et
// c'est CELUI-CI que lit `flexi-engine.js`, donc les deux écrans branchés
// dessus (calcul flexi, déclaration Dimona) ont travaillé 2 jours sur l'ancien
// plancher. Deux erreurs en sens inverse : accepter 11,87–12,10 €/h devenu
// illégal, refuser 21,01–21,42 €/h parfaitement légal.
//
// LA CAUSE N'EST PAS « quelqu'un a oublié de mettre à jour » : c'est qu'il y
// avait deux endroits à mettre à jour. Un seul reste, `lois-belges.js` (R1).
// Ce fichier ne fait plus que DÉRIVER — ne jamais y réintroduire une valeur
// horaire en dur, même « pour aller vite ».
// Verrous : `__tests__/un-chiffre-un-endroit-rmmmg-flexi.test.js` (compare les
// copies entre elles) et `chiffres-legaux-guard.js` (refuse le retour des
// anciennes valeurs dans du texte affiché, .html compris).
import { LB } from './lois-belges'
// R9 — round2 s'IMPORTE, il ne se redéfinit pas : la forme locale
// `Math.round(n*100)/100` rend NaN sur null, celle de money.js rend 0.
import { round2 } from './money'

const FLEXI_LB = LB.cotisations.flexiJob
const FLEXI_DATE_EFFET = FLEXI_LB.dateEffet

export const LB_FLEXI = {
  _meta: {
    version: '2026.8.31',
    dateMAJ: '2026-08-31',
    // L'extension EST en vigueur (01/07/2026) ; la prochaine échéance est
    // l'arrêté royal annuel qui prend effet au 1er janvier, sur les demandes
    // d'opt-out reçues avant le 30 septembre.
    prochainePrevue: '2027-01-01',
    source: 'ONSS, SPF Finances, Prestataire B, Prestataire C, Prestataire D, Moniteur belge',
  },

  // ═══ COTISATIONS ONSS ═══
  cotisationPatronale: FLEXI_LB.patronal,   // 28% — source dans lois-belges.js
  cotisationTravailleur: FLEXI_LB.travailleur, // 0% — exonération totale ONSS
  pecuelVacancesRatio: FLEXI_LB.peculeVacances, // 7,67% — source dans lois-belges.js

  // ═══ PLAFONDS FISCAUX ANNUELS ═══
  // 2026 = 18 440 € (indexation) ; 18 000 € était la valeur 2025.
  // Sources: SPF Finances ex.imp. 2026 ; Securex/SD Worx/SST 2026 ; HR Alert.
  plafondAnnuelNonPensionnes: FLEXI_LB.plafond,               // source dans lois-belges.js
  plafondAnnuelPensionnesAnticipes: FLEXI_LB.plafondPensionAnticipee, // source dans lois-belges.js
  plafondAnnuelPensionnesNormaux: null,        // pas de plafond

  /**
   * Le plafond fiscal du flexi-salaire pour une ANNÉE DE REVENUS (non-pensionné). Une année absente de `plafondParAnnee` (pas encore indexée chez nous) retombe sur
   * le plafond courant : la valeur est alors probablement périmée, et c'est à relire à chaque indexation (SPF Finances, page « Flexi-jobs »).
   */
  plafondPourAnnee(annee) {
    const parAnnee = FLEXI_LB.plafondParAnnee || {}
    const v = parAnnee[Number(annee)]
    return Number.isFinite(v) ? v : FLEXI_LB.plafond
  },

  // ═══ FLEXI-SALAIRE HORECA CP 302 ═══
  // ⚠️ Plancher HORECA seulement. Hors horeca, le minimum est le barème sectoriel
  // de la fonction (ou le RMMMG) et le maximum 150 % de ce barème (ONSS, instructions flexi-jobs 2026/3, lues le 23/09/2026).
  // Ce commentaire disait jusqu'au 23/09/2026 que la CP 302 était le « seul »
  // plancher, pour tous les secteurs : c'était faux.
  horeca: {
    // Date d'effet du montant SERVI, pas d'un montant historique.
    dateEffet: FLEXI_DATE_EFFET,
    salaireHoraireMin: FLEXI_LB.salaireMin,
    // Dérivés, jamais stockés : 11,87 × 7,67 % faisait 0,91, et ce 0,91 serait
    // resté faux après l'indexation exactement comme le 11,87.
    get peculeVacancesMin() { return round2(FLEXI_LB.salaireMin * FLEXI_LB.peculeVacances) },
    get totalHoraireMin() { return round2(FLEXI_LB.salaireMin * (1 + FLEXI_LB.peculeVacances)) },
  },

  // ═══ PLAFONDS FLEXI-SALAIRE TOUS SECTEURS ═══
  plafondMaxPourcentageBareme: 1.5,   // 150% barème sectoriel
  // ⚠️ 21 € était la valeur d'avant l'indexation du 01/09/2026 (21,42 €).
  // Un plafond périmé REFUSE un salaire légal — l'erreur se voit moins qu'un
  // plancher trop bas, elle coûte quand même un dossier bloqué à l'écran.
  plafondMaxHoraire: FLEXI_LB.salaireMax,

  // ═══ CONDITIONS D'ÉLIGIBILITÉ ═══
  eligibilite: {
    tauxEmploiMin: 0.8,                          // 4/5 temps
    trimestreVerifTMinus3: 3,
    trimestreFallbackTMinus5: 5,
    pensionnesEligibles: true,
    pensionAnticipeeEligible: true,
    interdictionEmployeurPrincipalOuLie: true,   // loi 2024
  },

  // ═══ EXTENSION TOUS SECTEURS — EN VIGUEUR DEPUIS LE 01/07/2026 ═══
  //
  // ⚠️ CORRIGÉ LE 31/08/2026. Ce bloc annonçait « reportée, en cours d'avis du
  // Comité A », un statut daté du 24/06/2026 — donc figé AVANT que la mesure
  // n'entre en vigueur. Deux mois durant, l'app a donc répondu qu'un flexi-job
  // était impossible dans des secteurs où il l'est.
  //
  // ✅ Source PRIMAIRE, lue le 31/08/2026 dans les instructions administratives
  // de l'ONSS, mot pour mot : « Depuis le 1er juillet 2026, le système des
  // flexi-jobs a, en principe, été étendu à l'ensemble des secteurs privés et
  // publics. Un nombre limité de secteurs et d'activités reste toutefois exclu. »
  //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/socialsecuritycontributions/calculationbase/flexi/scope.html
  //   https://www.socialsecurity.be/employer/instructions/dmfa/fr/latest/instructions/socialsecuritycontributions/calculationbase/flexi/opt-IN_opt-out.html
  extensionTousSecteurs: {
    dateEntreeEnVigueur: '2026-07-01',
    statut: 'en_vigueur',
    optOutSectoriel: true,
    fonctionsSoinsOuvertes: true,
    // Une demande d'exclusion (ou de ré-autorisation) doit parvenir à l'ONSS au
    // plus tard le 30 SEPTEMBRE pour entrer en vigueur au 1er janvier suivant.
    // C'est une échéance annuelle : elle vaut réponse à « mon secteur peut-il
    // encore sortir du dispositif ? ».
    dateLimiteDemandeOptOut: '09-30',
    effetOptOut: '01-01',
    source: "ONSS, instructions administratives DmfA — « Champ d'application » et « Extension et limitation du champ d'application (Opt-in et Opt-out) », source PRIMAIRE lue le 2026-08-31",
  },

  // ═══ LES SECTEURS QUI RESTENT EXCLUS (opt-outs) ═══
  //
  // ⚠️ LA LISTE COMPLÈTE, ET C'EST LE POINT. Une liste d'exclusions INCOMPLÈTE
  // est pire qu'une absence de liste : elle fait répondre « oui, flexi possible »
  // sur un secteur qui ne l'admet pas. Relevée en entier sur la page ONSS le
  // 31/08/2026 — CINQ commissions paritaires, pas trois.
  //
  // ⚠️ Chaque exclusion se lit avec sa CATÉGORIE D'EMPLOYEUR : plusieurs ne sont
  // que PARTIELLES, et c'est la catégorie qui tranche, pas la CP seule.
  secteursExclusOptOut: [
    {
      // ⚠️ LA CP NE SUFFIT PAS ICI, ET L'ERREUR SERAIT COÛTEUSE DANS LES DEUX
      // SENS. La CP 323 s'appelle « Gestion d'immeubles, agents immobiliers,
      // travailleurs domestiques » (cf. all-cp-belges.js) : elle couvre DEUX
      // métiers très différents. L'ONSS n'exclut que la catégorie d'employeur
      // **037**, celle des gens de maison. Un agent immobilier de la même CP
      // reste ADMIS au flexi.
      // Exclure « la CP 323 » tout court refuserait un flexi à une agence
      // immobilière qui y a droit ; ne rien exclure en ouvrirait un à un
      // employeur de personnel domestique qui n'y a pas droit.
      cp: '323', libelle: 'Gens de maison (et eux seuls dans la CP 323)',
      categoriesEmployeur: ['037'], portee: 'partielle',
      exception: "les autres métiers de la CP 323 — gestion d'immeubles, agents immobiliers — restent ADMIS : seule la catégorie d'employeur 037 est exclue",
    },
    {
      cp: '320', libelle: 'Entrepreneurs de pompes funèbres',
      categoriesEmployeur: ['320'], portee: 'partielle',
      exception: "les ouvriers qui exercent des activités de travailleur occasionnel, déclarées avec la notion 'E' dans la zone « Extra » d'« Occupation – Informations »",
    },
    {
      cp: '144', libelle: 'Agriculture',
      categoriesEmployeur: ['193', '293'], portee: 'totale',
    },
    {
      cp: '145', libelle: 'Horticulture',
      categoriesEmployeur: ['194', '494', '594'], portee: 'partielle',
      exception: "l'aménagement et/ou l'entretien de parcs et jardins reste ADMIS — seules les catégories 194, 494 et 594 sont exclues",
    },
    {
      cp: '143', libelle: 'Pêche maritime',
      portee: 'partielle',
      exception: 'le personnel à terre (catégorie 019) et le personnel des entrepôts (catégorie 086) restent ADMIS',
    },
  ],

  // ═══ TITRES-SERVICES — ADMIS, MAIS 3 MOIS SEULEMENT ═══
  // Règle propre à ce secteur, lue sur la même page ONSS le 31/08/2026 : le
  // flexi y est possible « seulement pendant les 3 premiers mois après la
  // première déclaration Dimona dans l'entreprise ». Au-delà, la poursuite de
  // l'occupation n'est possible qu'en CDI ordinaire, avec les cotisations
  // classiques. Répondre « oui, titres-services » sans cette limite ferait
  // monter un dossier qui devient irrégulier au 4e mois.
  titresServices: {
    admis: true,
    dureeMaxMois: 3,
    pointDeDepart: 'première déclaration Dimona dans l’entreprise',
    apres: 'CDI ordinaire et cotisations de sécurité sociale classiques',
  },

  // ═══ BONUS CCT 90 APPLIQUÉ AU FLEXI (nouveau 2026) ═══
  //
  // ⚠️ LES DEUX TAUX NE SONT PLUS ICI — et ce n'est pas un oubli.
  // Ce bloc portait `cotisationONSSPatronale: 0.33` et
  // `cotisationSolidariteTravailleur: 0.1307`, c'est-à-dire les MÊMES deux taux
  // que `LB.bonusCCT90` (lois-belges.js), recopiés sous d'AUTRES noms de clés.
  // Deux écritures d'un même taux, que rien n'obligeait à rester d'accord : le
  // jour où la CCT 90 change, on en corrige une et l'autre survit en silence.
  // 📏 Mesuré avant de les retirer (R2 sexies) : `grep -rn` sur app/, __tests__/
  // et scripts/ ne rend AUCUN lecteur de ces deux clés. Le moteur qui calcule le
  // bonus (`bonus-cct90-engine.js`) lit `LOIS_BELGES.bonusCCT90`, jamais celles-ci.
  // Zéro appelant, donc suppression sans effet — c'est le futur lecteur qu'on
  // protège. Les taux se lisent dans `LB.bonusCCT90` : 33 % employeur,
  // 13,07 % solidarité travailleur, avec leurs plafonds et leur source.
  //
  // Ce qui RESTE ici est le seul fait réellement propre au flexi : l'année à
  // partir de laquelle le bonus CCT 90 lui est applicable.
  bonusCCT90: {
    applicableDepuisAnnee: 2026,
  },

  // ═══ 🕰️ LISTE HISTORIQUE — NE PLUS S'EN SERVIR POUR RÉPONDRE ═══
  //
  // ⚠️ Depuis le 01/07/2026, la question « ce secteur est-il éligible ? » ne se
  // lit PLUS dans une liste blanche : tous les secteurs privés et publics le
  // sont, SAUF ceux de `secteursExclusOptOut`. Le raisonnement s'est inversé.
  //
  // Cette liste devient donc un HISTORIQUE de l'état d'avant l'extension. La
  // garder telle quelle sans le dire serait dangereux : elle contient 144, 145,
  // 320 et 323, que l'ONSS exclut aujourd'hui — quelqu'un qui la lirait comme
  // une liste d'éligibilité répondrait exactement l'inverse de la règle.
  //
  // 📏 Mesuré le 31/08/2026 avant de la laisser en place : AUCUN fichier de
  // l'app ne la lit (`grep -rn secteursEligiblesActuels app/`). Elle est donc
  // sans effet aujourd'hui — c'est le futur lecteur qu'on protège, pas le code.
  _historique_secteursEligiblesAvant_01_07_2026: [
    '302',     // Horeca (origine 2015)
    '201',     // Commerce détail indépendant
    '202',     // Commerce alimentation
    '202.01',  // Commerce alimentation moyenne distribution
    '118.03',  // Boulangeries
    '314',     // Coiffure / soins de beauté
    '311',     // Grande distribution
    '312',     // Grands magasins
    '144',     // Agriculture
    '145',     // Horticulture
    '223',     // Sport
    '225',     // Éducation
    '322.01',  // Titres-services (partiel)
    '320',     // Pompes funèbres
    '323',     // Immobilier
    '112',     // Garages
    '140.05',  // Déménagement
  ],

  // ═══ SOURCES ═══
  _sources: [
    'ONSS — socialsecurity.be/site_fr/employer/infos/flexi-jobs.htm',
    'Prestataire B — Indexation flexi-salaire CP 302 au 01/03/2026',
    'Prestataire C Lex4You — Traitement social flexi-job (28/01/2026)',
    'Prestataire D — Extension flexi-jobs (23/02/2026)',
    'Moniteur belge — 30/12/2025 — Loi plafond 18 000€',
    'Fédération Horeca Wallonie — flexi-salaire 2026',
  ],
}
